// قراردادها (فرم اصلی)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePager, CountBadge } from '../../components/paging';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';
import ContractorBlackList from '../../components/ContractorBlackList';
import ContractorAccounts from '../../components/ContractorAccounts';
import ContractTransActionList from './ContractTransActionList';

const toFa   = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng  = s => String(s ?? '').replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmtNum = n => (n ? Number(n).toLocaleString('fa-IR') : '•');

// آیتم‌های مناقصه
const MONAGHESE = ['مناقصه بزرگ', 'مناقصه متوسط', 'ترک تشریفات', 'مناقصه کوچک', 'مزایده'];

const EMPTY = {
  contract_no: '', estimated_coef: '', prepayment_percent: '', contract_date: '', subject: '',
  contractor_code: 0, contractor: '', counselor_code: 0, counselor: '',
  supervisor_id: 0, supervisor: '', executive_domain_id: 0, executive_domain: '',
  contract_type_id: 0, contract_type: '', tamin_branch_id: 0, tamin_branch: '',
  contract_cost: '', well_done_zemanat: '', virtual_code: 0, monaghese: 0, is_active: true,
  begin_date: '', duration: '', end_date: '',
  permission_no: '', permission_date: '', insurance_code: '', insurance_row: '', pay_id: '',
  notification_no: '', notification_date: '', assign_land_no: '', assign_land_date: '',
  equip_no: '', equip_date: '', article46_no: '', article46_date: '', article48_no: '', article48_date: '',
  temp_delivery_no: '', temp_delivery_date: '', final_delivery_no: '', final_delivery_date: '',
  end_of_contract_no: '', end_of_contract_date: '', mofasa_no: '', mofasa_date: '',
  present_package_date: '', remark: '',
};

const API = (path, opts = {}) => fetch(`/api/contract${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

/* ── جعبه جستجوی انتخابی (Lookup) — همان الگوی بخش بودجه ── */
function Lookup({ label, value, endpoint, onPick, flex = 1, disabled }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const search = async (text) => {
    setQ(text);
    const j = await API(`${endpoint}?q=${encodeURIComponent(text)}`);
    if (j.ok) { setRows(j.rows); setOpen(true); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex, position: 'relative' }}>
      <label style={ui.mlabel}>{label}</label>
      <input value={value || q} onFocus={() => !disabled && search(q)} onChange={e => !disabled && search(e.target.value)}
        placeholder="جستجو..." disabled={disabled} style={{ ...ui.input, background: disabled ? '#f2f2f2' : '#fff' }} />
      {value && !disabled && <button onClick={() => { onPick({ id: 0, display: '' }); setQ(''); }}
        style={{ position: 'absolute', left: '6px', top: '30px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#c62828', fontSize: '14px' }}>✕</button>}
      {open && rows.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', right: 0, left: 0, background: '#fff', border: '1px solid #e0e0e0',
          borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', zIndex: 700, maxHeight: '240px', overflowY: 'auto', marginTop: '2px' }}>
          {rows.map(r => (
            <div key={r.id} onClick={() => { onPick(r); setOpen(false); setQ(''); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>{r.display}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── ورودی متنی ساده ── */
function TInput({ label, val, onCh, flex = 1, type = 'text' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex }}>
      <label style={ui.mlabel}>{label}</label>
      <input type={type} value={val} onChange={e => onCh(e.target.value)} style={ui.input} />
    </div>
  );
}

/* ── جفت شماره/تاریخ (برای ابلاغ، تحویل‌ها، ...) ── */
function NoDate({ label, noVal, dateVal, onNo, onDate }) {
  return (
    <div style={ui.row}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
        <label style={ui.mlabel}>{`شماره ${label}`}</label>
        <input value={noVal} onChange={e => onNo(e.target.value)} style={ui.input} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
        <label style={ui.mlabel}>{`تاریخ ${label}`}</label>
        <JalaliDatePicker value={dateVal} onChange={onDate} placeholder={`تاریخ ${label}`} />
      </div>
    </div>
  );
}

export default function ContractList({ user, onBack }) {
  // دکمه‌های فرم FRMCONTRACT — دیدن = دسترسی
  const vbf = useFormButtons('FRMCONTRACT', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK","BTNBLACKLIST","BTNCONTRACTORACCOUNT"]);
  const [blackList, setBlackList] = useState(null);
  const [transAction, setTransAction] = useState(null);
  /* حساب‌های پیمانکار و پیشنهاد ثبت حساب برای پیمانکار بدون حساب */
  const [accounts, setAccounts] = useState(null);
  const [accountAsk, setAccountAsk] = useState(null);
  const accountNecessary = useRef(true);
  const [data, setData]         = useState([]);
  const pager = usePager();          // صفحه‌بندی مشترک — ۲۰۰ ردیف در هر صفحه
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [toast, setToast]       = useState('');
  const [tab, setTab]           = useState('main');

  const showToast = m => { setToast(m); setTimeout(() => setToast(''), 3000); };

  const openAccounts = (autoNew = false) => {
    if (!selected || !vbf.can('BTNCONTRACTORACCOUNT')) return;
    setAccounts({ code: selected.contractor_code, name: selected.contractor, contract: selected.id,
      title: `${selected.contract_no || ''} - ${selected.contract_date || ''}`, autoNew });
  };
  // پیمانکاری که هنوز شماره حساب ندارد: فقط به کسی که اجازهٔ ثبت حساب دارد پیشنهاد می‌شود
  useEffect(() => {
    if (!selected || !accountNecessary.current || !vbf.can('BTNCONTRACTORACCOUNT')) return;
    let alive = true;
    API(`/contractor-accounts/need/?contractor=${selected.contractor_code || 0}&contract=${selected.id || 0}`).then(j => {
      if (alive && j.ok && j.missing && j.can_new && accountNecessary.current) setAccountAsk(selected.id);
    });
    return () => { alive = false; };
  }, [selected?.id]); // eslint-disable-line


  // ── ستون‌های گرید (ستون‌های پرکاربرد) ──
  const cols = [
    { h: 'کد مجازی', k: 'virtual_code', w: 80 },
    { h: 'شماره', k: 'contract_no', w: 110 },
    { h: 'تاریخ', k: 'contract_date', w: 90, mono: true },
    { h: 'موضوع', k: 'subject', w: 240, right: true },
    { h: 'پیمانکار', k: 'contractor', w: 220, right: true },
    { h: 'مبلغ قرارداد', k: 'contract_cost', w: 130, num: true, color: '#1b5e20' },
    { h: 'افزایش/کاهش', k: 'diff_cost', w: 120, num: true, color: '#6a1b9a' },
    { h: 'جمع مبلغ قرارداد', k: 'sum_contract_cost', w: 140, num: true, color: '#1a237e' },
    { h: 'جمع صورت وضعیت کارکرد', k: 'sum_request_work', w: 150, num: true, color: '#00695c' },
    { h: 'تعداد صورت وضعیت', k: 'request_count', w: 90 },
    { h: 'نوع قرارداد', k: 'contract_type', w: 160, right: true },
    { h: 'حوزه اجرایی', k: 'executive_domain', w: 150, right: true },
    { h: 'ناظر', k: 'supervisor', w: 130, right: true },
    { h: 'شعبه تامین اجتماعی', k: 'tamin_branch', w: 150, right: true },
    { h: 'میزان ضمانت', k: 'well_done_zemanat', w: 120, num: true, color: '#e65100' },
    { h: 'شروع', k: 'begin_date', w: 90, mono: true },
    { h: 'مدت', k: 'duration', w: 60 },
    { h: 'خاتمه', k: 'end_date', w: 90, mono: true },
    { h: 'وضعیت', k: 'is_active', w: 70, status: true },
  ];
  const [colW, setColW] = useState(cols.map(c => c.w));
  const colWRef = useRef(colW);
  useEffect(() => { colWRef.current = colW; }, [colW]);
  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => setColW(w => { const n = [...w]; n[idx] = Math.max(40, sw + (sx - ev.clientX)); return n; });
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);

  const load = useCallback(async (pg = 1, sr = search) => {
    setLoading(true); setError(''); if (pg === 1) setSelected(null);
    const j = await API(`/contracts/?page=${pg}&search=${encodeURIComponent(sr)}`);
    setLoading(false);
    if (j.ok) {
      setData(prev => pager.merge(prev, j, pg));
    } else setError(j.msg);
  }, [search]);

  useEffect(() => { load(1, ''); }, []);   // eslint-disable-line

  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const openNew  = () => { if (!vbf.can('BTNNEW')) return; setForm(EMPTY); setError(''); setTab('main'); setModal('new'); };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({ ...EMPTY, ...selected });
    setError(''); setTab('main'); setModal('edit');
  };
  const openDel  = () => { if (!vbf.can('BTNDELETE')) return; if (selected) setModal('delete'); };

  const save = async () => {
    setSaving(true); setError('');
    const j = modal === 'new'
      ? await API('/contracts/create/', { method: 'POST', body: JSON.stringify(form) })
      : await API(`/contracts/${selected.id}/update/`, { method: 'PUT', body: JSON.stringify(form) });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast(j.msg); load(1); }
    else setError(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await API(`/contracts/${selected.id}/delete/`, { method: 'DELETE' });
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast('حذف شد'); load(1); }
    else { setError(j.msg); setModal(null); }
  };

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{ ...ui.btnEdit, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={openDel} disabled={!selected} style={{ ...ui.btnDel, opacity: selected ? 1 : 0.4, cursor: selected ? 'pointer' : 'default' }}>✕ حذف</button>}
        {vbf.can('BTNBLACKLIST') && <button onClick={() => selected && setBlackList({ code: selected.contractor_code, name: selected.contractor })}
          disabled={!selected} style={{ ...ui.btnDel, background: 'linear-gradient(135deg,#7f0000,#c62828)', opacity: selected ? 1 : 0.4 }}>⛔ Black List</button>}
        {vbf.can('BTNCONTRACTORACCOUNT') && <button onClick={() => openAccounts(false)} disabled={!selected}
          style={{ ...ui.btnEdit, background: 'linear-gradient(135deg,#4527a0,#5e35b1)', opacity: selected ? 1 : 0.4 }}>🏦 حساب پیمانکار</button>}
        <button onClick={() => selected && setTransAction({ id: selected.id, subject: `${selected.contract_no || ''} - ${selected.subject || ''}` })}
          disabled={!selected} style={{ ...ui.btnEdit, background: 'linear-gradient(135deg,#00695c,#00897b)', opacity: selected ? 1 : 0.4 }}>📋 تمدید قرارداد</button>
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load(1, search)}
        style={{ ...ui.searchInput, flex: 1, minWidth: '180px' }} placeholder="جستجو (شماره، موضوع یا پیمانکار)..." />
      <button onClick={() => load(1, search)} style={ui.btnEdit}>🔍 جستجو</button>
      {loading && <span>⏳</span>}
      <CountBadge shown={data.length} total={pager.total} loading={loading} />
    </>
  );

  
  const cell = (r, c, sel) => {
    const tc = sel ? '#fff' : '#111';
    if (c.status) return <td style={{ ...ui.td, color: sel ? '#fff' : r.is_active ? '#1b5e20' : '#b71c1c' }}>{r.is_active ? 'فعال' : 'غیرفعال'}</td>;
    if (c.num)    return <td style={{ ...ui.tdNum, color: sel ? '#fff' : r[c.k] ? c.color : '#ccc' }}>{fmtNum(r[c.k])}</td>;
    if (c.mono)   return <td style={{ ...ui.td, fontFamily: 'monospace', fontSize: '11px', color: tc }}>{r[c.k] || '•'}</td>;
    return <td style={{ ...ui.td, textAlign: c.right ? 'right' : 'center', fontSize: '11px', color: tc, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r[c.k]}>{c.k === 'virtual_code' || c.k === 'request_count' ? toFa(r[c.k]) : (r[c.k] || '•')}</td>;
  };

  return (
    <>
      <ListPage user={user} onBack={onBack} title="قراردادها" subtitle="بخش قراردادها — سامانه ارجان"
        toolbar={toolbar} error={!modal ? error : ''} pager={pager} loading={loading} onMore={p => load(p, search)}>
        <table style={{ ...ui.table, tableLayout: 'fixed' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
            <tr>
              {cols.map((c, i) => (
                <th key={i} style={{ ...ui.th, width: colW[i], minWidth: 40, position: 'relative', userSelect: 'none' }}>
                  {c.h}<span onMouseDown={e => startResize(i, e)} style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize' }} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((r, i) => {
              const sel = selected?.id === r.id;
              const bg  = sel ? '#1a2f50' : i % 2 === 0 ? '#fff' : '#fdf6e3';
              return (
                <tr key={r.id} style={{ background: bg, cursor: 'pointer' }}
                  onClick={() => setSelected(sel ? null : r)} onDoubleClick={() => { setSelected(r); setTimeout(openEdit, 0); }}
                  onMouseEnter={e => { if (!sel) e.currentTarget.style.background = '#e8f0fe'; }}
                  onMouseLeave={e => { if (!sel) e.currentTarget.style.background = bg; }}>
                  {cols.map((c, ci) => <React.Fragment key={ci}>{cell(r, c, sel)}</React.Fragment>)}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && data.length === 0 && <div style={ui.empty}>📭 قراردادی یافت نشد</div>}
      </ListPage>

      {(modal === 'new' || modal === 'edit') && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '860px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? '＋ قرارداد جدید' : `✎ ویرایش قرارداد ${toFa(form.contract_no)}`}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>

            {/* تب‌های داخلی فرم */}
            <div style={{ display: 'flex', gap: '4px', padding: '8px 16px 0', background: '#f8f9fa', flexWrap: 'wrap' }}>
              {[['main', 'اطلاعات اصلی'], ['parties', 'طرف‌های قرارداد'], ['dates', 'تاریخ‌ها و تحویل‌ها'], ['other', 'مجوز و بیمه']].map(([k, t]) => (
                <button key={k} onClick={() => setTab(k)}
                  style={{ padding: '7px 14px', border: 'none', borderRadius: '8px 8px 0 0', cursor: 'pointer',
                    fontFamily: 'inherit', fontSize: '12px', fontWeight: '700',
                    background: tab === k ? '#fff' : 'transparent', color: tab === k ? '#4a148c' : '#888',
                    borderBottom: tab === k ? '2px solid #4a148c' : '2px solid transparent' }}>{t}</button>
              ))}
            </div>

            <div style={ui.mBody}>
              {error && <div style={ui.errBox}>⚠️ {error}</div>}

              {tab === 'main' && (<>
                <div style={ui.row}>
                  <TInput label="شماره قرارداد" val={form.contract_no} onCh={v => F('contract_no', v)} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={ui.mlabel}>تاریخ قرارداد</label>
                    <JalaliDatePicker value={form.contract_date} onChange={v => F('contract_date', v)} placeholder="تاریخ قرارداد" />
                  </div>
                  <TInput label="کد مجازی" val={toFa(form.virtual_code || '')} onCh={() => {}} flex={0.6} />
                </div>
                <div style={ui.row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 2 }}>
                    <label style={ui.mlabel}>موضوع قرارداد</label>
                    <input value={form.subject} onChange={e => F('subject', e.target.value)} style={ui.input} />
                  </div>
                </div>
                <div style={ui.row}>
                  <TInput label="مبلغ قرارداد (ریال)" val={form.contract_cost} onCh={v => F('contract_cost', v)} type="number" />
                  <TInput label="ضریب پیمان" val={form.estimated_coef} onCh={v => F('estimated_coef', v)} />
                  <TInput label="درصد پیش پرداخت" val={form.prepayment_percent} onCh={v => F('prepayment_percent', v)} type="number" />
                  <TInput label="میزان ضمانت" val={form.well_done_zemanat} onCh={v => F('well_done_zemanat', v)} type="number" />
                </div>
                <div style={ui.row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={ui.mlabel}>نوع مناقصه</label>
                    <select value={form.monaghese} onChange={e => F('monaghese', Number(e.target.value))} style={ui.input}>
                      {MONAGHESE.map((m, i) => <option key={i} value={i}>{m}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, paddingTop: '18px' }}>
                    <input type="checkbox" id="isActive" checked={form.is_active} onChange={e => F('is_active', e.target.checked)} style={{ width: '18px', height: '18px' }} />
                    <label htmlFor="isActive" style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>فعال</label>
                  </div>
                </div>
              </>)}

              {tab === 'parties' && (<>
                <div style={ui.row}>
                  <Lookup label="پیمانکار" value={form.contractor} endpoint="/contracts/lookup/contractor/"
                    onPick={r => setForm(f => ({ ...f, contractor_code: r.id, contractor: r.display }))} flex={2} />
                </div>
                <div style={ui.row}>
                  <Lookup label="مشاور" value={form.counselor} endpoint="/contracts/lookup/contractor/"
                    onPick={r => setForm(f => ({ ...f, counselor_code: r.id, counselor: r.display }))} flex={2} />
                </div>
                <div style={ui.row}>
                  <Lookup label="ناظر" value={form.supervisor} endpoint="/contracts/lookup/supervisor/"
                    onPick={r => setForm(f => ({ ...f, supervisor_id: r.id, supervisor: r.display }))} />
                  <Lookup label="حوزه اجرایی" value={form.executive_domain} endpoint="/contracts/lookup/executive-domain/"
                    onPick={r => setForm(f => ({ ...f, executive_domain_id: r.id, executive_domain: r.display }))} />
                </div>
                <div style={ui.row}>
                  <Lookup label="نوع قرارداد" value={form.contract_type} endpoint="/contracts/lookup/contract-type/"
                    onPick={r => setForm(f => ({ ...f, contract_type_id: r.id, contract_type: r.display }))} />
                  <Lookup label="شعبه تامین اجتماعی" value={form.tamin_branch} endpoint="/contracts/lookup/tamin-branch/"
                    onPick={r => setForm(f => ({ ...f, tamin_branch_id: r.id, tamin_branch: r.display }))} />
                </div>
              </>)}

              {tab === 'dates' && (<>
                <div style={ui.row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={ui.mlabel}>تاریخ شروع</label>
                    <JalaliDatePicker value={form.begin_date} onChange={v => F('begin_date', v)} placeholder="تاریخ شروع" />
                  </div>
                  <TInput label="مدت (روز)" val={form.duration} onCh={v => F('duration', v)} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={ui.mlabel}>تاریخ خاتمه</label>
                    <JalaliDatePicker value={form.end_date} onChange={v => F('end_date', v)} placeholder="تاریخ خاتمه" />
                  </div>
                </div>
                <div style={ui.divider} />
                <NoDate label="ابلاغ" noVal={form.notification_no} dateVal={form.notification_date} onNo={v => F('notification_no', v)} onDate={v => F('notification_date', v)} />
                <NoDate label="تحویل زمین" noVal={form.assign_land_no} dateVal={form.assign_land_date} onNo={v => F('assign_land_no', v)} onDate={v => F('assign_land_date', v)} />
                <NoDate label="تجهیز کارگاه" noVal={form.equip_no} dateVal={form.equip_date} onNo={v => F('equip_no', v)} onDate={v => F('equip_date', v)} />
                <NoDate label="ماده ۴۶" noVal={form.article46_no} dateVal={form.article46_date} onNo={v => F('article46_no', v)} onDate={v => F('article46_date', v)} />
                <NoDate label="ماده ۴۸" noVal={form.article48_no} dateVal={form.article48_date} onNo={v => F('article48_no', v)} onDate={v => F('article48_date', v)} />
                <NoDate label="تحویل موقت" noVal={form.temp_delivery_no} dateVal={form.temp_delivery_date} onNo={v => F('temp_delivery_no', v)} onDate={v => F('temp_delivery_date', v)} />
                <NoDate label="تحویل قطعی" noVal={form.final_delivery_no} dateVal={form.final_delivery_date} onNo={v => F('final_delivery_no', v)} onDate={v => F('final_delivery_date', v)} />
                <NoDate label="اتمام قرارداد" noVal={form.end_of_contract_no} dateVal={form.end_of_contract_date} onNo={v => F('end_of_contract_no', v)} onDate={v => F('end_of_contract_date', v)} />
                <NoDate label="مفاصاحساب" noVal={form.mofasa_no} dateVal={form.mofasa_date} onNo={v => F('mofasa_no', v)} onDate={v => F('mofasa_date', v)} />
                <div style={ui.row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={ui.mlabel}>آخرین روز پیشنهاد قیمت</label>
                    <JalaliDatePicker value={form.present_package_date} onChange={v => F('present_package_date', v)} placeholder="آخرین روز پیشنهاد قیمت" />
                  </div>
                </div>
              </>)}

              {tab === 'other' && (<>
                <div style={ui.row}>
                  <TInput label="شماره مجوز" val={form.permission_no} onCh={v => F('permission_no', v)} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={ui.mlabel}>تاریخ مجوز</label>
                    <JalaliDatePicker value={form.permission_date} onChange={v => F('permission_date', v)} placeholder="تاریخ مجوز" />
                  </div>
                </div>
                <div style={ui.row}>
                  <TInput label="شماره بیمه" val={form.insurance_code} onCh={v => F('insurance_code', v)} />
                  <TInput label="ردیف پیمان" val={form.insurance_row} onCh={v => F('insurance_row', v)} />
                  <TInput label="شناسه پرداخت" val={form.pay_id} onCh={v => F('pay_id', v)} />
                </div>
                <div style={ui.row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                    <label style={ui.mlabel}>توضیح</label>
                    <input value={form.remark} onChange={e => F('remark', e.target.value)} style={ui.input} />
                  </div>
                </div>
              </>)}
            </div>

            <div style={ui.mFooter}>
              <button onClick={() => { setModal(null); setError(''); }} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳ ...' : '💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal === 'delete' && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '440px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...ui.mHeader, background: 'linear-gradient(135deg,#b71c1c,#c62828)' }}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{ ...ui.mBody, textAlign: 'center', padding: '26px' }}>
              <div style={{ fontSize: '34px', marginBottom: '10px' }}>🗑️</div>
              قرارداد شماره {toFa(selected?.contract_no)}<br />{selected?.contractor}
            </div>
            <div style={ui.mFooter}>
              <button onClick={() => setModal(null)} style={ui.btnCancel}>خیر</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{ ...ui.btnSave, background: '#b71c1c' }}>{saving ? '⏳...' : 'بله، حذف'}</button>}
            </div>
          </div>
        </div>
      )}

      {blackList && <ContractorBlackList contractorCode={blackList.code} contractorName={blackList.name}
        onClose={() => setBlackList(null)} />}
      {transAction && <ContractTransActionList contractId={transAction.id} contractSubject={transAction.subject}
        onClose={() => setTransAction(null)} />}
      {accounts && <ContractorAccounts contractorCode={accounts.code} contractorName={accounts.name}
        contractId={accounts.contract} contractTitle={accounts.title} autoNew={accounts.autoNew}
        onChanged={m => showToast(m)} onClose={() => setAccounts(null)} />}
      {accountAsk && selected?.id === accountAsk && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,.45)', zIndex: 100400, display: 'flex',
          alignItems: 'center', justifyContent: 'center', direction: 'rtl' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 22px', maxWidth: 440, width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,.3)', fontFamily: "'Vazirmatn',Tahoma,sans-serif" }}>
            <div style={{ fontWeight: 700, color: '#4527a0', marginBottom: 6 }}>انتخاب کنید</div>
            <div style={{ fontSize: 14, marginBottom: 16 }}>شماره حساب پیمانکار ثبت نشده است</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button onClick={() => { setAccountAsk(null); openAccounts(true); }}
                style={{ ...ui.btnSave, background: '#4527a0' }}>شماره حساب را الان ثبت میکنم</button>
              <button onClick={() => setAccountAsk(null)} style={ui.btnEdit}>بعداً یادآوری کن</button>
              <button onClick={() => { accountNecessary.current = false; setAccountAsk(null); }}
                style={{ ...ui.btnEdit, background: '#78909c' }}>دیگر این پیام را نشان نده</button>
            </div>
          </div>
        </div>)}
      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}
